import React, { useEffect, useState } from "react";
import { Form, Select, Spin, Tag } from "antd";
import { USER_MODAL_TYPE } from "../constants";
import { getInceptionPermissionGroups, getInceptionUserPermissionGroups } from "./inceptionActions";
import { UserServiceColumn, UserServiceFormSectionProps } from "./types";

const FormItem = Form.Item;

export const INCEPTION_CONNECTION_STATUS = {
    CONNECTED: "CONNECTED",
    NOT_CONNECTED: "NOT_CONNECTED"
};

const toInceptionGroupOptions = (groups: Array<any>) => {
    return groups
        .map((group: any) => ({
            label: group?.name || group?.id,
            value: group?.id
        }))
        .filter((group: any) => !!group.value)
        .sort((a: any, b: any) =>
            String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base", numeric: true })
        );
};

const getAssignedFlag = (response: any) => {
    const flags = [
        response?.isAssigned,
        response?.isTenantAssigned,
        response?.tenantAssigned,
        response?.hasAssignedService,
        response?.hasServiceAssigned,
        response?.isConfigured
    ];

    return flags.find((flag) => typeof flag === "boolean");
};

const isTenantAssignedToInception = (
    response: any,
    permissionGroups: Array<any>,
    assignedPermissionGroupIds: Array<string>
) => {
    const explicitFlag = getAssignedFlag(response);
    if (typeof explicitFlag === "boolean") {
        return explicitFlag;
    }

    return permissionGroups.length > 0 || assignedPermissionGroupIds.length > 0;
};

const getInceptionStatusLabel = (status: string | undefined) => {
    switch (status) {
        case INCEPTION_CONNECTION_STATUS.CONNECTED:
            return "Connected";
        case INCEPTION_CONNECTION_STATUS.NOT_CONNECTED:
            return "Not connected";
        default:
            return "";
    }
};

const renderInceptionStatus = (row: any) => {
    const label = getInceptionStatusLabel(row?.inceptionConnectionStatus);
    if (!label) {
        return <span>-</span>;
    }

    if (row?.inceptionConnectionStatus === INCEPTION_CONNECTION_STATUS.CONNECTED) {
        return <Tag color="green">Connected</Tag>;
    }

    return <Tag>Not connected</Tag>;
};

export const InceptionUserServiceSection = ({
    form,
    mode,
    editRowData,
    tenants,
    selectedTenantId
}: UserServiceFormSectionProps) => {
    const [isLoadingInceptionGroups, setIsLoadingInceptionGroups] = useState(false);
    const [isInceptionAssigned, setIsInceptionAssigned] = useState(false);
    const [inceptionGroupOptions, setInceptionGroupOptions] = useState<Array<{ label: string, value: string }>>([]);

    const selectedTenant = tenants.find((tenant: any) => tenant.tenantId === selectedTenantId);
    const userId = editRowData?.userId;
    const inceptionConnectionStatus = editRowData?.inceptionConnectionStatus;

    const canManageInceptionPermissionGroups = isInceptionAssigned
        && (mode === USER_MODAL_TYPE.CREATE || inceptionConnectionStatus === INCEPTION_CONNECTION_STATUS.CONNECTED);

    useEffect(() => {
        let isMounted = true;

        const resetInceptionState = () => {
            if (!isMounted) {
                return;
            }

            setIsInceptionAssigned(false);
            setInceptionGroupOptions([]);
            form.setFieldsValue({ inceptionPermissionGroupIds: [] });
        };

        const loadInceptionGroups = async () => {
            if (!selectedTenant) {
                resetInceptionState();
                return;
            }

            if (mode === USER_MODAL_TYPE.EDIT && inceptionConnectionStatus !== INCEPTION_CONNECTION_STATUS.CONNECTED) {
                resetInceptionState();
                return;
            }

            try {
                setIsLoadingInceptionGroups(true);

                const response = mode === USER_MODAL_TYPE.EDIT && userId
                    ? await getInceptionUserPermissionGroups(selectedTenant.buildingId, selectedTenant.tenantId, userId)
                    : await getInceptionPermissionGroups(selectedTenant.buildingId, selectedTenant.tenantId);

                if (!isMounted) {
                    return;
                }

                const permissionGroups = Array.isArray(response?.permissionGroups) ? response.permissionGroups : [];
                const assignedPermissionGroupIds = Array.isArray(response?.assignedPermissionGroupIds)
                    ? response.assignedPermissionGroupIds
                    : [];
                const isAssigned = isTenantAssignedToInception(response, permissionGroups, assignedPermissionGroupIds);

                setIsInceptionAssigned(isAssigned);
                setInceptionGroupOptions(toInceptionGroupOptions(permissionGroups));
                form.setFieldsValue({
                    inceptionPermissionGroupIds: isAssigned ? assignedPermissionGroupIds : []
                });
            } catch (error) {
                console.error("Error loading inception permission groups", error);
                resetInceptionState();
            } finally {
                if (isMounted) {
                    setIsLoadingInceptionGroups(false);
                }
            }
        };

        loadInceptionGroups();

        return () => {
            isMounted = false;
        };
    }, [form, inceptionConnectionStatus, mode, selectedTenant, userId]);

    return (
        <>
            {isLoadingInceptionGroups && (
                <FormItem>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Spin size="small" />
                        <span>Loading tenant services...</span>
                    </div>
                </FormItem>
            )}
            {isInceptionAssigned && mode === USER_MODAL_TYPE.EDIT && !canManageInceptionPermissionGroups && (
                <FormItem>
                    <div style={{ color: "rgba(0, 0, 0, 0.65)" }}>
                        Inception permission groups can be managed only when the user is connected to Inception.
                    </div>
                </FormItem>
            )}
            {canManageInceptionPermissionGroups && (
                <FormItem
                    label="Inception Permission Groups"
                    name="inceptionPermissionGroupIds"
                    preserve={false}
                    rules={[{
                        required: mode === USER_MODAL_TYPE.CREATE && inceptionGroupOptions.length > 0,
                        message: "Please select one or more Inception permission groups"
                    }]}
                >
                    <Select
                        className="inception-permission-groups-select"
                        mode="multiple"
                        placeholder="Select Inception permission groups"
                        loading={isLoadingInceptionGroups}
                        options={inceptionGroupOptions}
                        maxTagCount="responsive"
                        maxTagTextLength={32}
                        allowClear
                    />
                </FormItem>
            )}
        </>
    );
};

export const getInceptionUserServiceColumns = (users: Array<any> = []): Array<UserServiceColumn> => {
    const showInceptionStatusColumn = users.some((user: any) => !!user?.inceptionConnectionStatus);

    if (!showInceptionStatusColumn) {
        return [];
    }

    return [{
        title: "Inception Status",
        dataIndex: "inceptionConnectionStatus",
        sorter: (a: any, b: any) => getInceptionStatusLabel(a?.inceptionConnectionStatus)
            .localeCompare(getInceptionStatusLabel(b?.inceptionConnectionStatus)),
        render: (_: string, row: any) => renderInceptionStatus(row)
    }];
};
